import React, { useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import useAppState from "./store/appState";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { IntlProvider } from "react-intl";
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { Tolgee, DevTools, TolgeeProvider, FormatSimple, LanguageDetector, LanguageStorage, useTolgee } from "@tolgee/react";

import ReactGA from "react-ga4";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const baseUrl = document.getElementsByTagName("base")[0].getAttribute("href");
const rootElement = document.getElementById("root");
const root = createRoot(rootElement);

function TranslationsProvider({ children }) {
  const translations = useAppState((state) => state.translations);
  const loading = useAppState((state) => state.translationsLoading);
  const error = useAppState((state) => state.translationsError);
  const loadTranslations = useAppState((state) => state.getTranslationsData);

  useEffect(() => {
    loadTranslations();
  }, [loadTranslations]);

  const tolgee = useMemo(
    () =>
      translations &&
      Tolgee()
        .use(DevTools())
        .use(FormatSimple())
        .use(LanguageDetector())
        .use(LanguageStorage())
        .init({
          staticData: translations,
          availableLanguages: Object.keys(translations),
          defaultLanguage: "en",
        }),
    [translations],
  );

  const fallback = (
    <div>
      <svg aria-hidden="true" className="w-10 h-10 mx-auto mt-5  text-gray-200 animate-spin fill-red-sp" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    </div>
  );

  if (error || loading || !tolgee) return fallback;

  return (
    <TolgeeProvider tolgee={tolgee} fallback={fallback}>
      {children}
    </TolgeeProvider>
  );
}

ReactGA.initialize("G-387734574");
ReactGA.send("pageview");

function AppWrapper() {
  const setIsMobile = useAppState((state) => state.setIsMobile);
  const tolgee = useTolgee(["language"]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    handleResize(); // Set the initial value

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setIsMobile]);

  return (
    <IntlProvider locale={tolgee.getLanguage()}>
      <QueryClientProvider client={queryClient}>
        <App />
        {/* <ReactQueryDevtools/> */}
      </QueryClientProvider>
    </IntlProvider>
  );
}

root.render(
  <BrowserRouter>
    <TranslationsProvider>
      <AppWrapper />
    </TranslationsProvider>
  </BrowserRouter>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
